import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import svgJar from 'ember-svg-jar/helpers/svg-jar';
import EmberPopover from 'ember-tooltips/components/ember-popover';
import { and, not } from 'ember-truth-helpers';
import type FeatureFlagsService from 'garaje/services/feature-flags';
import {
  MatrixKind,
  displayDescriptionKeyMapping,
  displayNameKeyMapping,
  displayNameFlagMapping,
} from 'garaje/utils/permission-sets';
import type { MatrixByProduct, MatrixItem, MatrixRow } from 'garaje/utils/permission-sets';

interface CreateProductPermissionsSignature {
  Args: {
    productMatrix: MatrixByProduct;
    permissionSetIds: Array<string>;
    addPermission: (item: MatrixItem) => void;
    removePermission: (item: MatrixItem) => void;
    copiedFrom: string | null;
  };
}

export default class CreateProductPermissionsComponent extends Component<CreateProductPermissionsSignature> {
  @service declare featureFlags: FeatureFlagsService;

  @tracked collapsed = false;

  get productRow(): string {
    const { productMatrix } = this.args;
    return productMatrix && productMatrix.product
      ? displayNameKeyMapping[productMatrix.product.displayNameKey] || ''
      : '';
  }

  // Return helper function with access to feature flags service
  get disableRow(): (row: MatrixRow) => boolean {
    return (row: MatrixRow) => {
      const rowFeatureFlag = displayNameFlagMapping[row.displayNameKey] || null;

      // If a relevant feature flag is specified, the row is disabled if the FF is OFF
      if (rowFeatureFlag) return !this.featureFlags.isEnabled(rowFeatureFlag);

      return !row.enabled;
    };
  }

  @action
  handleCollapseAndExpand(): void {
    this.collapsed = !this.collapsed;
  }

  @action
  togglePermission(row: MatrixRow): void {
    if (this.disableRow(row)) return;

    const item = row.item;

    if (item) {
      const isPermissionSelected = this.isRowSelected(row);

      if (isPermissionSelected) {
        this.args.removePermission(item);
      } else {
        this.args.addPermission(item);
      }
    }
  }

  @action
  isRowSelected(row: MatrixRow): boolean {
    return this.args.permissionSetIds.includes(row.item?.id || '');
  }

  isRowCategory(row: MatrixRow): boolean {
    return row.kind === MatrixKind.CATEGORY;
  }

  renderRowName(row: MatrixRow): string {
    return displayNameKeyMapping[row.displayNameKey] || '';
  }

  renderRowDescription(row: MatrixRow): string {
    return displayDescriptionKeyMapping[row.displayDescriptionKey] || '';
  }

  <template>
    <div class="mb-2" data-test-create-product-permissions>
      <div
        class="flex items-center w-full py-2 pl-5 bg-gem-10 cursor-pointer"
        {{on "click" this.handleCollapseAndExpand}}
        data-test-product-header
      >
        <button type="button" class="pt-2" data-test-chevron>
          {{#if this.collapsed}}
            {{svgJar "chevron-down" height="15" width="15"}}
          {{else}}
            {{svgJar "chevron-up" height="15" width="15"}}
          {{/if}}
        </button>
        <h3 class="pl-3 pt-1" data-test-product-name>{{this.productRow}}</h3>
      </div>

      {{#unless this.collapsed}}
        <ul role="list" class="w-full">
          {{#each @productMatrix.rows as |row|}}
            {{#if (this.isRowCategory row)}}
              <div class="flex items-center py-2 bg-[#F2F4FA]" data-test-category-row>
                <span class="ml-12 pt-1 text-center text-gem-80" data-test-category-name>
                  {{this.renderRowName row}}
                </span>
              </div>
            {{else}}
              <div class="flex items-center border-b py-2 border-carbon-10 w-full" data-test-permission-row>
                <div class="grid grid-cols-10 space-x-5 w-full">
                  <div class="flex col-span-1 items-center justify-end">
                    <button
                      type="button"
                      class="pt-2 !opacity-100 {{unless row.enabled 'cursor-default'}}"
                      disabled={{this.disableRow row}}
                      {{on "click" (fn this.togglePermission row)}}
                      data-test-permission-checkbox
                    >
                      {{#if (and row.selected (this.disableRow row))}}
                        {{svgJar "checkbox-selected-disabled"}}
                      {{else if (and (not row.selected) (this.disableRow row))}}
                        {{svgJar "checkbox-unchecked-disabled"}}
                      {{else if (this.isRowSelected row)}}
                        {{svgJar "checkbox-selected"}}
                      {{else}}
                        {{svgJar "checkbox-unchecked"}}
                      {{/if}}
                    </button>
                  </div>

                  <div
                    class="flex col-span-4 items-center {{if row.enabled 'cursor-pointer' 'cursor-default'}}"
                    {{on "click" (fn this.togglePermission row)}}
                    data-test-permission-name-area
                  >
                    <span
                      class="pt-1 {{unless row.enabled 'text-carbon-60'}}"
                      data-test-permission-name
                    >{{this.renderRowName row}}</span>
                  </div>

                  <div class="flex col-span-5 items-center">
                    <span class="pt-1 mr-5 text-carbon-50" data-test-permission-description>
                      {{this.renderRowDescription row}}
                    </span>
                  </div>

                  {{#if (and @copiedFrom (not row.enabled))}}
                    <EmberPopover
                      @side="bottom"
                      @tooltipClass="rounded-sm"
                      @arrowClass="hidden"
                      @spacing={{15}}
                      @delay={{500}}
                    >
                      <span class="text-carbon-70 font-normal">
                        Copied from
                        {{@copiedFrom}}
                      </span>
                    </EmberPopover>
                  {{/if}}
                </div>
              </div>
            {{/if}}
          {{/each}}
        </ul>
      {{/unless}}
    </div>
  </template>
}
